import axios, { AxiosError } from "axios";
import { useCallback, useContext } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import { ADMIN_API_URL } from "../../../utils/env";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

export interface IDailyKillSwitchStatus {
  tripped: boolean;
  used: string;
  threshold: string;
}

// useKillSwitch wraps GET /daily-kill-switch for status polling plus the
// two mutation endpoints (reset + set-threshold). Status is polled every
// 10s so the UI reflects trips / admin retunes without a manual refresh;
// mutations revalidate immediately via swr.mutate() so the status box
// flips right after the admin clicks a button.
export const useKillSwitch = () => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const { addToast } = useToast();

  const endpoint = ADMIN_API_URL(AdminEndpointEnum.DAILY_KILL_SWITCH);
  const swr = useSWR<IDailyKillSwitchStatus, AxiosError>(
    tokenId ? [endpoint, tokenId] : undefined,
    async () => {
      const response = await axios.get<IDailyKillSwitchStatus>(
        endpoint,
        queryConfig()
      );
      return response.data;
    },
    {
      refreshInterval: 10000,
    }
  );

  const withAuthHeaders = useCallback(
    () => ({
      headers: {
        Authorization: `Bearer ${tokenId}`,
      },
    }),
    [tokenId]
  );

  const handleSuccess = useCallback(
    (header: string, subheader?: string) => {
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header,
          subheader,
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    },
    [addToast]
  );

  const handleError = useCallback(
    (error: AxiosError, header: string) => {
      // eslint-disable-next-line no-console
      console.error(error);
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header,
          subheader: ((error as AxiosError)?.response?.data as any)?.error,
          status: ToastStatusEnum.ERROR,
        },
        4000
      );
    },
    [addToast]
  );

  const resetDailyKillSwitch = useCallback(async () => {
    try {
      await axios.post(
        ADMIN_API_URL(AdminEndpointEnum.RESET_DAILY_KILL_SWITCH),
        {},
        withAuthHeaders()
      );
      handleSuccess(
        "Kill switch reset",
        "Counter zeroed and tripped flag cleared"
      );
      swr.mutate();
    } catch (e) {
      handleError(e as AxiosError, "Reset kill switch failed");
    }
  }, [handleError, handleSuccess, swr, withAuthHeaders]);

  const setDailyKillSwitchThreshold = useCallback(
    async (threshold: number) => {
      try {
        await axios.post(
          ADMIN_API_URL(AdminEndpointEnum.DAILY_KILL_SWITCH_THRESHOLD),
          { threshold },
          withAuthHeaders()
        );
        handleSuccess(
          "Threshold updated",
          `New daily cap: $${threshold.toLocaleString()}`
        );
        swr.mutate();
      } catch (e) {
        handleError(e as AxiosError, "Set threshold failed");
      }
    },
    [handleError, handleSuccess, swr, withAuthHeaders]
  );

  return {
    ...swr,
    resetDailyKillSwitch,
    setDailyKillSwitchThreshold,
  };
};
